import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCollections } from '@/src/modules/catalog/useCollections';
import { CatalogPricingProvider, useCatalogPricingMode } from '@/src/modules/catalog/useCatalogPricingMode';
import { ProductCard } from './ProductCard';

const GRID_CLASSES = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';

function SkeletonGrid() {
  return (
    <div className={GRID_CLASSES}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-72 rounded-[2rem] bg-surface border border-outline-variant/30 animate-pulse"
        />
      ))}
    </div>
  );
}

function CatalogPageContent() {
  const [page, setPage] = useState(1);
  const { data, meta, loading, error, reload } = useCollections({ page });
  const { isPartnerUser, remainingCredits, walletBalance, balanceFormatted } = useCatalogPricingMode();

  // If the backend ever clamps an out-of-range page (e.g. `?page=99` when
  // only 2 pages exist), fall back to whatever page it actually returned so
  // the Prev/Next buttons never disable against a page the server isn't on.
  //
  // Deliberately depends on `meta.page` alone, NOT `page` — `useCollections`
  // does not clear `meta` while a new page is loading (it only tracks
  // `loading`/`error`), so `meta` is briefly stale-behind-`page` on every
  // click. Adding `page` to these deps re-runs this effect on that stale
  // render and immediately reverts the click (verified: it turns the fetch
  // sequence page=1→2→1, so Next never advances past page 1 — a real
  // regression a prior version of this fix introduced and
  // `CatalogPage.test.tsx` didn't catch because it mocks `meta.page` to
  // already match the requested page, which never happens against the real
  // hook). Do not "fix" this eslint-disable without a test against the real
  // `useCollections` hook proving the fetch sequence is `[1, 2]`, not `[1, 2, 1]`.
  useEffect(() => {
    if (meta && meta.page !== page) setPage(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.page]);

  const location = useLocation();
  const initialBanner = (location.state as { catalogBanner?: string } | null)?.catalogBanner ?? null;
  const [banner, setBanner] = useState<string | null>(initialBanner);

  return (
    <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
      {banner && (
        <div className="mb-8 flex items-center justify-between gap-4 rounded-2xl border border-status-failed/20 bg-status-failed/10 px-5 py-3 text-sm font-semibold text-status-failed">
          <span>{banner}</span>
          <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss">
            &times;
          </button>
        </div>
      )}

      {isPartnerUser && (
        <div
          data-testid="partner-catalog-banner"
          className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-4"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-primary">Partner Wholesale Catalog</span>
            </div>
            <p className="mt-1 text-sm text-on-surface-variant">
              Every design is unlocked from your wallet balance.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="text-sm font-semibold text-on-surface">
              Balance: <strong className="text-primary font-bold">{balanceFormatted ?? (walletBalance !== undefined && walletBalance > 0 ? `₹${walletBalance.toLocaleString('en-IN')}` : remainingCredits > 0 ? `${remainingCredits} ${remainingCredits === 1 ? 'credit' : 'credits'}` : `₹${remainingCredits ?? 0}`)}</strong>
            </span>
            <Link
              to="/dashboard/partner?tab=credits"
              className="text-xs font-bold text-primary hover:underline bg-primary/10 px-3 py-1.5 rounded-lg"
            >
              + Buy Credits
            </Link>
          </div>
        </div>
      )}

      <div className="mb-10 text-center">
        <h1 className="text-4xl font-bold text-on-surface">Choose your invitation</h1>
        <p className="mt-3 text-lg text-on-surface-variant">
          Every design, ready to personalize and publish.
        </p>
      </div>

      {loading && <SkeletonGrid />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-4 rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-sm font-medium text-status-failed">{error}</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="text-sm font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && data.length === 0 && (
        <div className="rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-lg font-semibold text-on-surface">Nothing available right now</p>
          <p className="mt-2 text-sm text-on-surface-variant">
            Check back soon — new invitation designs are added regularly.
          </p>
        </div>
      )}

      {!loading && !error && data && data.length > 0 && (
        <>
          <div className={GRID_CLASSES}>
            {data.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          {meta && meta.totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-4 text-sm text-on-surface-variant">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-outline-variant/40 px-4 py-2 font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span>
                Page {meta.page} of {meta.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-outline-variant/40 px-4 py-2 font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CatalogPage() {
  return (
    <CatalogPricingProvider>
      <CatalogPageContent />
    </CatalogPricingProvider>
  );
}

export default CatalogPage;
